import React from 'react';
import c from 'classnames';
import type { SearchRequest } from 'searchkit';

import type { SearchkitWrapperProps } from '@uncinc/react-searchkit/dist/components/Wrapper';

import SearchkitWrapper from '../Search/Wrapper';
import { APP_OPTIONS, SEARCHKIT_HITS_PER_PAGE_EVENTS, SEARCHKIT_HOST_URL } from '../../config/constants';
import { runtimeMappings } from '../SearchTopicalEvents/config';
import { resultAttributes } from './config';

// Subcomponents.
import Results from './Results';

interface SearchEventsSubComponents {
  Results: typeof Results;
}

export interface SearchEventsProps extends Omit<SearchkitWrapperProps, 'connection' | 'searchSettings' | 'indexName'> {
  targetAudienceId?: number;
}

const SearchEvents: React.FC<SearchEventsProps> & SearchEventsSubComponents = (props) => {
  const {
    targetAudienceId,
    className,
    children,
    ...restProps
  } = props;

  const classNames = c('SearchEvents', className);

  return (
    <SearchkitWrapper
      {...restProps}
      future={{
        preserveSharedStateOnUnmount: true,
      }}
      className={classNames}
      connection={{
        host: SEARCHKIT_HOST_URL,
      }}
      indexName={APP_OPTIONS.parameters.searchkitIndexEvents}
      hitsPerPage={SEARCHKIT_HITS_PER_PAGE_EVENTS}
      searchSettings={{
        search_attributes: [],
        result_attributes: resultAttributes,
        runtime_mappings: runtimeMappings,
      }}
      requestOptions={{
        hooks: {
          beforeSearch: (requests: SearchRequest[]) => {
            requests.forEach((request) => {
              request.body.query = {
                bool: {
                  must: [
                    // This makes sure we only fetch events where
                    // start date >= today
                    {
                      range: {
                        event_date: {
                          gte: 'now/d',
                        },
                      },
                    },
                  ],
                  should: [
                    // Get all events and boost the ones that have
                    // a connection with a target audience.
                    {
                      function_score: {
                        query: {
                          match_all: {},
                        },
                        ...(targetAudienceId && {
                          functions: [
                            {
                              filter: {
                                term: {
                                  target_audience_id: targetAudienceId,
                                },
                              },
                              weight: 10,
                            },
                          ],
                        }),
                        score_mode: 'multiply',
                        boost_mode: 'multiply',
                      },
                    },
                  ],
                  filter: [
                    {
                      exists: {
                        field: 'computed_first_upcoming_date_index',
                      },
                    },
                  ],
                },
              };
            });

            return Promise.resolve(requests);
          },
        },
      }}
    >
      {children}
    </SearchkitWrapper>
  );
};

SearchEvents.Results = Results;

export default SearchEvents;
